import { FC, memo, useCallback, useEffect, useState } from "react";

const DynamicRelativeTime: FC<{ dateString: string }> = memo(({ dateString }) => {
    const [relativeTime, setRelativeTime] = useState<string>("");

    // Function to format relative time
    const calculateRelativeTime = useCallback((dateStr: string): string => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        // Less than a minute
        if (diffInSeconds < 60) {
            return `${diffInSeconds}s ago`;
        }

        // Less than an hour
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        }

        // Less than a day
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        }

        // Less than a week
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        }

        // A week or more
        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks}w ago`;
    }, []);

    useEffect(() => {
        // Initial calculation
        setRelativeTime(calculateRelativeTime(dateString));

        // Set up interval to update the time every second
        const intervalId = setInterval(() => {
            setRelativeTime(calculateRelativeTime(dateString));
        }, 1000);

        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, [dateString, calculateRelativeTime]);

    return <div className="text-sm">{relativeTime}</div>;
});

DynamicRelativeTime.displayName = "DynamicRelativeTime";

export default DynamicRelativeTime;
